
import React from 'react'
import { useDataQuery } from '@dhis2/app-runtime'
import { Card } from '@dhis2/ui'
import MetricCard from './MetricCard' // We'll need to create this or use your existing one

// ProgressBar component (same as in Objectives.jsx)
const ProgressBar = ({ value, height = '6px', borderRadius = '3px' }) => (
  <div style={{
    width: '100%',
    height,
    backgroundColor: '#e5e7eb',
    borderRadius
  }}>
    <div style={{
      width: `${Math.min(Math.max(value || 0, 0), 100)}%`,
      height: '100%',
      backgroundColor: getProgressColor(value || 0),
      borderRadius,
      transition: 'width 0.3s ease'
    }} />
  </div>
);

const getProgressColor = (progress) => {
  if (progress >= 80) return '#28a745';
  if (progress >= 50) return '#ffc107';
  return '#dc3545';
};

const TaskMetrics = ({ orgUnitId, performanceData }) => {
  const { loading, error, data } = useDataQuery({
    tasks: {
      resource: 'tracker/events',
      params: {
        program: 'yiT00XKrrHP',
        programStage: 'Nv9nYNcBTdM',
        pageSize: 100,
        fields: 'event,orgUnit,orgUnitName,eventDate,dueDate,dataValues[dataElement,value]'
      }
    }
  })

  const effectiveTasks = performanceData?.tasks?.events || data?.tasks?.instances || []

  const calculateMetrics = (tasks) => {
    if (!tasks || tasks.length === 0) {
      return {
        totalTasks: 0,
        completedTasks: 0,
        overdueTasks: 0,
        highPriorityTasks: 0,
        completionRate: 0
      }
    }

    const now = new Date();
    
    let completedTasks = 0;
    let overdueTasks = 0;
    let highPriorityTasks = 0;
    let totalProgress = 0;

    tasks.forEach(task => {
      const dataValues = task.dataValues || [];
      
      const progress = parseInt(dataValues.find(dv => dv.dataElement === 'AElpNxzTdRh')?.value) || 0;
      const status = dataValues.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value || 'not_started';
      const dueDate = dataValues.find(dv => dv.dataElement === 'IY0xyOmlEr7')?.value;
      
      // Count completed tasks (progress = 100 or status = completed)
      if (progress === 100 || status === 'completed') {
        completedTasks++;
      }
      
      // Count overdue tasks
      if (dueDate && new Date(dueDate) < now && progress < 100) {
        overdueTasks++;
      }
      
      // Count high priority tasks (you might need to add a priority field)
      // For now, we'll consider tasks with progress < 50% as high priority
      if (progress < 50) {
        highPriorityTasks++;
      }
      
      totalProgress += progress;
    });

    const totalTasks = tasks.length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    return {
      totalTasks,
      completedTasks,
      overdueTasks,
      highPriorityTasks,
      completionRate,
      averageProgress: totalTasks > 0 ? Math.round(totalProgress / totalTasks) : 0
    }
  }

  if (loading && !performanceData) return (
    <Card style={{ padding: 16, margin: 18 }}>
      <h4>Task Management Overview</h4>
      <ProgressBar />
      <div style={{ textAlign: 'center', marginTop: 8 }}>Loading task metrics...</div>
    </Card>
  )

  if (error && !performanceData) return (
    <Card style={{ padding: 16, backgroundColor: '#f8d7da' }}>
      <h4>Task Management Overview</h4>
      <div style={{ color: '#721c24' }}>
        Error loading task metrics: {error.message}
      </div>
    </Card>
  )

  const metrics = calculateMetrics(effectiveTasks);

  return (
    <Card style={{ padding: 16, marginBottom: 24 }}>
      <h4 style={{ marginBottom: 16, margin: 18 }}>Task Management Overview</h4>
      
      <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
          gap: 24,
          margin: 18,          
        }}>
          <MetricCard 
            title="Total Tasks" 
            value={metrics.totalTasks} 
            color="#007bff"
            description="All assigned tasks across the organization"
          />
          <MetricCard 
            title="Completed" 
            value={metrics.completedTasks} 
            subtitle={`${metrics.completionRate}% completion rate`}
            color="#28a745"
            description="Successfully finished tasks"
          />
          <MetricCard 
            title="Overdue" 
            value={metrics.overdueTasks} 
            color="#dc3545"
            description="Tasks past their due date"
          />
          <MetricCard 
            title="High Priority" 
            value={metrics.highPriorityTasks} 
            color="#ffc107"
            description="Critical tasks requiring immediate attention"
          />
        </div>

      
      {/* Progress bar for overall completion */}
      {metrics.totalTasks > 0 && (
        <div style={{ marginTop: 16, margin: 18 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: 8,
            fontSize: 14 
          }}>
            <span style={{fontSize: 14}}>Overall Progress</span>
            <span>{metrics.averageProgress}%</span>
          </div>
          <ProgressBar
            value={metrics.averageProgress} 
            max={100} 
            style={{ height: 8, borderRadius: 4 }}
          />
        </div>
      )}
    </Card>
  )
}

export default TaskMetrics
